import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { showToast } from 'stores/ToastStore';
import Tooltip from 'components/v2/Tooltip';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import GlobeAltIcon from 'components/v2/icons/GlobeAltIcon';
import SessionStore from 'stores/SessionStore';
import { getDeviceHash } from 'utils/browser';
import { REGIONS } from 'constants/Regions';
import {
  AVAILABLE_LOCALE,
  THAI_WHITELIST,
  US_WHITELIST,
} from '../../constants/LocaleConstants';
import { fetchResourceBundle } from '../../utils/i18WebAPIUtils';
import { LocaleContext } from '../../providers/locale-settings-context';

const LanguageSelector = ({ mobile = false }) => {
  const { setLanguageCodeId } = useContext(LocaleContext);
  const { t } = useTranslation();
  const forceCloseRef = useRef();
  const deviceHash = getDeviceHash();
  let languageCode = SessionStore.getLanguagePreference(deviceHash) || 'en';
  const [currentLanguage, setCurrentLanguage] = useState(languageCode);

  const languageButton = mobile ? (
    t('CHANGE_LANGUAGE')
  ) : (
    <GlobeAltIcon size="medium" />
  );

  /*
   * We need to remove resource bundle since we don't want a fallback to preloaded language
   * To do that we use i18n.removeResourceBundle
   */
  const handleLanguageChange = async (language) => {
    try {
      const deviceHash = getDeviceHash();
      const id = `${deviceHash}::language`;
      SessionStore.setLanguagePreference(id, language);
      const { translations, regionDataDetails, languageCodeId } =
        await fetchResourceBundle(language);
      setLanguageCodeId(regionDataDetails?.languageCodeId);
      i18n.removeResourceBundle(currentLanguage, 'translations');
      i18n.addResourceBundle(language, 'translations', translations);
      i18n.changeLanguage(language);
      SessionStore.setLanguageCodeIdPreference(
        `${deviceHash}::languageCodeId`,
        languageCodeId,
      );
      setCurrentLanguage(language);
    } catch (e) {
      showToast(
        t('LANGUAGE_CHANGE_FAILED'),
        t('LANGUAGE_CHANGE_FAILED'),
        'error',
      );
    }
  };

  useLayoutEffect(() => {
    if (LANGUAGE_SELECTION_DISABLED) {
      setCurrentLanguage('en');
    }
  }, []);

  useEffect(() => {
    if (currentLanguage) {
      handleLanguageChange(currentLanguage);
    }
  }, [currentLanguage]);

  const handleCurrentLanguageChange = (language) => {
    if (LANGUAGE_SELECTION_DISABLED) {
      return showToast(
        t('LANGUAGE_CHANGE_NOT_ALLOWED'),
        t('LANGUAGE_CHANGE_NOT_ALLOWED'),
        'error',
      );
    }
    setCurrentLanguage(language);
  };

  const handleDropdownClose = () => {
    if (forceCloseRef.current) forceCloseRef.current.forceClose();
  };

  const getClassNames = (active) => {
    if (active) return 'nav-item w-full text-left text-teal-500 font-extrabold';
    return 'nav-item w-full text-left';
  };

  const body = () => {
    const availableLanguages = {};
    if (REGION_CODE === REGIONS.US) {
      US_WHITELIST.forEach((languageCode) => {
        availableLanguages[languageCode] = AVAILABLE_LOCALE[languageCode];
      });
    } else {
      THAI_WHITELIST.forEach((languageCode) => {
        availableLanguages[languageCode] = AVAILABLE_LOCALE[languageCode];
      });
    }
    if (mobile) {
      return (
        <select
          className="form-select"
          value={currentLanguage}
          onChange={(e) => handleCurrentLanguageChange(e.target.value)}
        >
          {Object.keys(availableLanguages).map((locale) => (
            <option
              key={availableLanguages[locale].value}
              value={availableLanguages[locale].value}
            >
              {`${t(availableLanguages[locale].localizationSlug)} (${
                availableLanguages[locale].value
              })`}
            </option>
          ))}
        </select>
      );
    }
    return (
      <DropdownMenu
        menuClosed={() => handleDropdownClose()}
        button={languageButton}
        key="right-nav"
      >
        {Object.keys(availableLanguages).map((locale) => (
          <Menu.Item key={locale}>
            <button
              onClick={(_e) => handleCurrentLanguageChange(locale)}
              className={getClassNames(locale === currentLanguage)}
              disabled={locale === currentLanguage}
            >
              {`${t(availableLanguages[locale].localizationSlug)} (${
                availableLanguages[locale].value
              })`}
            </button>
          </Menu.Item>
        ))}
      </DropdownMenu>
    );
  };

  if (mobile) {
    return body();
  }

  return (
    <div className="mt-1">
      <Tooltip
        id="language-selector-tooltip"
        ref={forceCloseRef}
        body={() => body()}
      >
        {t('CHANGE_LANGUAGE')}
      </Tooltip>
    </div>
  );
};

export default LanguageSelector;
